import React, { useEffect, useState } from "react";
import { ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Loader2, BarChart3 } from "lucide-react";
import { adminSeoHealthStatus, adminSeoHealthRunNow, adminSeoHealthHistory } from "@/lib/api";

const StatusBadge = ({ status }) => {
  const map = {
    ok: "bg-emerald-100 text-emerald-700 border-emerald-200",
    warn: "bg-amber-100 text-amber-700 border-amber-200",
    fail: "bg-red-100 text-red-700 border-red-200",
    pass: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${map[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {status}
    </span>
  );
};

const CheckIcon = ({ status }) => {
  if (status === "pass") return <CheckCircle2 size={15} className="text-emerald-600 shrink-0 mt-0.5" />;
  if (status === "warn") return <AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />;
  return <XCircle size={15} className="text-red-600 shrink-0 mt-0.5" />;
};

const GscSetupInstructions = ({ property }) => (
  <div className="bg-[#FFFBEB] border border-amber-200 rounded-xl p-5 text-sm text-amber-900 space-y-2" data-testid="seo-health-gsc-setup">
    <p className="font-semibold flex items-center gap-2"><BarChart3 size={16} /> Google Search Console koppeling nog niet geconfigureerd</p>
    <p>Met deze koppeling worden wekelijks indexeringscijfers (klikken, vertoningen, geïndexeerde URL's) opgehaald en krijg je een waarschuwing zodra indexering stagneert. Eenmalige setup:</p>
    <ol className="list-decimal pl-5 space-y-1">
      <li>Ga naar <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="underline">console.cloud.google.com</a> → maak (of kies) een project.</li>
      <li>APIs &amp; Services → Enable APIs → activeer <strong>"Google Search Console API"</strong>.</li>
      <li>IAM &amp; Admin → Service Accounts → maak een service account (bijv. <em>seo-monitor</em>) → tab "Keys" → Add key → <strong>JSON</strong> (download het bestand).</li>
      <li>In <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="underline">Search Console</a> → property <strong>{property}</strong> → Instellingen → Gebruikers en rechten → voeg het service-account e-mailadres (…@…iam.gserviceaccount.com) toe met <strong>Volledig</strong>.</li>
      <li>Plak de inhoud van het JSON-bestand in de chat — dan zet ik hem veilig in de backend-configuratie.</li>
    </ol>
  </div>
);

const GscMetrics = ({ gsc }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="seo-health-gsc-metrics">
    {[
      ["Klikken (28d)", gsc.total_clicks],
      ["Vertoningen (28d)", gsc.total_impressions],
      ["Unieke pagina's met vertoningen", gsc.unique_pages_with_impressions],
      ["Geïndexeerd / ingediend (sitemap)", `${gsc.sitemap_indexed} / ${gsc.sitemap_submitted}`],
    ].map(([label, value]) => (
      <div key={label} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">{label}</div>
        <div className="text-2xl font-bold text-[#0F172A] mt-1">{value?.toLocaleString?.() ?? value}</div>
      </div>
    ))}
  </div>
);

const SeoHealthPanel = () => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const reload = () => {
    adminSeoHealthStatus().then(setStatus).catch((e) => setError(e?.response?.data?.detail || e.message));
    adminSeoHealthHistory().then(setHistory).catch(() => {});
  };

  useEffect(() => { reload(); }, []);

  const runNow = async () => {
    setRunning(true);
    setError(null);
    try {
      await adminSeoHealthRunNow();
      reload();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  const report = status?.last_report;

  return (
    <div className="space-y-6" data-testid="admin-seo-health-tab">
      <div className="bg-gradient-to-br from-[#ECFDF5] to-white border border-[#D1FAE5] rounded-2xl p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl text-[#0F172A] flex items-center gap-2">
              <ShieldCheck size={22} className="text-emerald-600" /> SEO-gezondheidscheck
              {report && <StatusBadge status={report.status} />}
            </h2>
            <p className="text-sm text-[#64748B] mt-1 max-w-2xl">
              Controleert wekelijks (ma 04:00 UTC) of canonicals, hreflang en sitemaps op de live site consistent
              blijven en bewaakt de Google-indexering. Bij problemen krijg je automatisch een e-mail.
            </p>
            {report && (
              <p className="text-xs text-[#94A3B8] mt-2">
                Laatste run: {new Date(report.ts).toLocaleString()} ({report.source}) ·
                {" "}{report.checks_passed} pass / {report.checks_warned} warn / {report.checks_failed} fail ·
                Volgende geplande run: {status?.next_run ? new Date(status.next_run).toLocaleString() : "—"}
              </p>
            )}
          </div>
          <button
            onClick={runNow}
            disabled={running}
            data-testid="seo-health-run-now"
            className="hac-btn-primary inline-flex items-center gap-2 self-start"
          >
            {running ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {running ? "Bezig…" : "Nu uitvoeren"}
          </button>
        </div>
        {error && <p className="text-sm text-red-600 mt-3" data-testid="seo-health-error">{error}</p>}
      </div>

      {report?.alerts?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 space-y-2" data-testid="seo-health-alerts">
          <p className="font-semibold text-red-800 flex items-center gap-2"><AlertTriangle size={16} /> Indexering-waarschuwingen</p>
          {report.alerts.map((a, i) => (
            <p key={i} className="text-sm text-red-700">• {a.message}</p>
          ))}
        </div>
      )}

      {report?.gsc ? <GscMetrics gsc={report.gsc} /> :
        (!status?.gsc_configured && <GscSetupInstructions property={status?.gsc_property || "horseappcompare.com"} />)}
      {report?.gsc_error && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="seo-health-gsc-error">
          GSC-fout bij laatste run: {report.gsc_error}
        </p>
      )}

      {report?.gsc?.sitemaps?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
          <h3 className="font-heading text-lg text-[#0F172A] mb-3">Sitemaps in Google Search Console</h3>
          <div className="space-y-1.5">
            {report.gsc.sitemaps.map((s) => (
              <div key={s.path} className="flex items-center justify-between text-sm border-b border-[#F1F5F9] py-1.5 last:border-0">
                <span className="text-[#0F172A] font-mono text-xs truncate mr-4">{s.path}</span>
                <span className="text-[#64748B] shrink-0">
                  {s.indexed} geïndexeerd / {s.submitted} ingediend
                  {s.errors > 0 && <span className="text-red-600 ml-2">{s.errors} fouten</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {report?.checks && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="seo-health-checks">
          <h3 className="font-heading text-lg text-[#0F172A] mb-3">Technische checks (live site)</h3>
          <div className="space-y-2">
            {report.checks.map((c) => (
              <div key={c.id} className="flex items-start gap-2.5 text-sm">
                <CheckIcon status={c.status} />
                <div>
                  <span className="text-[#0F172A]">{c.label}</span>
                  {c.detail && <span className="text-[#94A3B8] ml-2 text-xs">{c.detail}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {history.length > 1 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
          <h3 className="font-heading text-lg text-[#0F172A] mb-3">Historie</h3>
          <div className="space-y-1.5">
            {history.map((h) => (
              <div key={h.ts} className="flex items-center justify-between text-sm border-b border-[#F1F5F9] py-1.5 last:border-0">
                <span className="text-[#64748B]">{new Date(h.ts).toLocaleString()} · {h.source}</span>
                <span className="flex items-center gap-3">
                  {h.gsc && (
                    <span className="text-xs text-[#94A3B8]">
                      {h.gsc.total_impressions?.toLocaleString()} vert. · {h.gsc.sitemap_indexed} geïndexeerd
                    </span>
                  )}
                  <StatusBadge status={h.status} />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SeoHealthPanel;
